import { EventEmitter } from 'events';
import {
  IBlueprintManager,
  IBlueprintFactory,
  IBlueprint,
  BlueprintManagerConfig,
  BlueprintSearchCriteria,
  BlueprintSearchResult,
  BlueprintGenerationResult,
  BlueprintGenerationContext,
  BlueprintUsageStatistics,
  BlueprintManagerEvents
} from '../types/blueprint-interfaces';
import { ICSElement } from '../types/interfaces';

/**
 * Менеджер шаблонов для управления коллекцией Blueprint
 */
export class BlueprintManager extends EventEmitter implements IBlueprintManager {
  public readonly config: BlueprintManagerConfig;
  public readonly factory: IBlueprintFactory;

  private blueprints = new Map<string, IBlueprint>();
  private cache = new Map<string, any>();
  private usageStats = new Map<string, BlueprintUsageStatistics>();

  constructor(
    factory: IBlueprintFactory,
    config: BlueprintManagerConfig = {}
  ) {
    super();
    this.factory = factory;
    this.config = {
      autoRegisterBuiltins: true,
      enableCaching: true,
      maxCacheSize: 100,
      defaultValidation: true,
      enableLogging: false,
      ...config
    };

    // Автоматическая регистрация встроенных шаблонов
    if (this.config.autoRegisterBuiltins) {
      this.registerBuiltinBlueprints();
    }
  }

  /**
   * Регистрация встроенных шаблонов
   */
  private async registerBuiltinBlueprints(): Promise<void> {
    try {
      const builtinTypes = this.factory.getAvailableTypes();
      for (const type of builtinTypes) {
        try {
          const blueprint = this.factory.createBlueprint(type);
          await this.registerBlueprint(blueprint);
        } catch (error) {
          if (this.config.enableLogging) {
            console.warn(`Не удалось зарегистрировать встроенный шаблон ${type}:`, error);
          }
        }
      }
    } catch (error) {
      if (this.config.enableLogging) {
        console.error('Ошибка регистрации встроенных шаблонов:', error);
      }
    }
  }

  /**
   * Регистрация шаблона
   */
  async registerBlueprint(blueprint: IBlueprint): Promise<void> {
    try {
      // Проверяем что шаблон не зарегистрирован
      if (this.blueprints.has(blueprint.id)) {
        throw new Error(`Шаблон с ID '${blueprint.id}' уже зарегистрирован`);
      }

      // Регистрируем шаблон
      this.blueprints.set(blueprint.id, blueprint);

      // Инициализируем статистику
      if (!this.usageStats.has(blueprint.id)) {
        this.usageStats.set(blueprint.id, {
          blueprintId: blueprint.id,
          usageCount: 0,
          lastUsed: new Date(),
          popularParameterCombinations: []
        });
      }

      // Эмитируем событие
      this.emit('blueprint-registered', { blueprint });

      if (this.config.enableLogging) {
        console.log(`Зарегистрирован шаблон: ${blueprint.name} (${blueprint.id})`);
      }
    } catch (error) {
      this.emit('blueprint-error', { 
        blueprintId: blueprint.id, 
        error: error instanceof Error ? error : new Error(String(error))
      });
      throw error;
    }
  }

  /**
   * Получение шаблона по ID
   */
  async getBlueprint(id: string): Promise<IBlueprint | null> {
    return this.blueprints.get(id) || null;
  }

  /**
   * Получение всех зарегистрированных шаблонов
   */
  async getAllBlueprints(): Promise<IBlueprint[]> {
    return Array.from(this.blueprints.values());
  }

  /**
   * Поиск шаблонов
   */
  async searchBlueprints(criteria: BlueprintSearchCriteria): Promise<BlueprintSearchResult> {
    const startTime = Date.now();
    let blueprints = Array.from(this.blueprints.values());

    // Фильтрация по тексту
    if (criteria.query) {
      const query = criteria.query.toLowerCase();
      blueprints = blueprints.filter(bp =>
        bp.name.toLowerCase().includes(query) ||
        bp.description.toLowerCase().includes(query) ||
        bp.metadata.tags?.some(tag => tag.toLowerCase().includes(query))
      );
    }

    // Фильтрация по типу
    if (criteria.type && criteria.type.length > 0) {
      blueprints = blueprints.filter(bp => criteria.type!.includes(bp.type));
    }

    // Фильтрация по категории
    if (criteria.category && criteria.category.length > 0) {
      blueprints = blueprints.filter(bp => criteria.category!.includes(bp.category));
    }

    // Фильтрация по тегам
    if (criteria.tags && criteria.tags.length > 0) {
      blueprints = blueprints.filter(bp =>
        criteria.tags!.some(tag =>
          bp.metadata.tags?.includes(tag)
        )
      );
    }

    // Фильтрация по автору
    if (criteria.author) {
      blueprints = blueprints.filter(bp =>
        bp.metadata.author?.toLowerCase().includes(criteria.author!.toLowerCase())
      );
    }

    // Сортировка
    if (criteria.sortBy) {
      blueprints.sort((a, b) => {
        let aValue: any, bValue: any;

        switch (criteria.sortBy) {
          case 'name':
            aValue = a.name;
            bValue = b.name;
            break;
          case 'created':
            aValue = a.metadata.created || new Date(0);
            bValue = b.metadata.created || new Date(0);
            break;
          case 'updated':
            aValue = a.metadata.updated || new Date(0);
            bValue = b.metadata.updated || new Date(0);
            break;
          case 'popularity':
            aValue = this.usageStats.get(a.id)?.usageCount || 0;
            bValue = this.usageStats.get(b.id)?.usageCount || 0;
            break;
          default:
            return 0;
        }

        if (criteria.sortOrder === 'desc') {
          return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
        } else {
          return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
        }
      });
    }

    // Пагинация
    const total = blueprints.length;
    const offset = criteria.offset || 0;
    const limit = criteria.limit || total;
    blueprints = blueprints.slice(offset, offset + limit);

    const searchTime = Date.now() - startTime;

    return {
      blueprints,
      total,
      searchTime,
      criteria
    };
  }

  /**
   * Удаление шаблона
   */
  async unregisterBlueprint(id: string): Promise<boolean> {
    const blueprint = this.blueprints.get(id);
    if (!blueprint) {
      return false;
    }

    this.blueprints.delete(id);
    this.usageStats.delete(id);

    // Очищаем кэш связанный с этим шаблоном
    for (const [key] of this.cache.entries()) {
      if (key.startsWith(id + ':')) {
        this.cache.delete(key);
      }
    }

    this.emit('blueprint-unregistered', { blueprintId: id });

    if (this.config.enableLogging) {
      console.log(`Удален шаблон: ${blueprint.name} (${id})`);
    }

    return true;
  }

  /**
   * Генерация структуры из шаблона
   */
  async generateFromBlueprint(
    blueprintId: string,
    parameters: Record<string, any>,
    options?: {
      parentElement?: ICSElement;
      idPrefix?: string;
      dryRun?: boolean;
    }
  ): Promise<BlueprintGenerationResult> {
    const blueprint = this.blueprints.get(blueprintId);
    if (!blueprint) {
      throw new Error(`Шаблон с ID '${blueprintId}' не найден`);
    }

    try {
      // Генерируем ключ кэша
      const cacheKey = this.config.enableCaching ? 
        `${blueprintId}:${JSON.stringify(parameters)}:${JSON.stringify(options || {})}` : 
        null;

      // Проверяем кэш
      if (cacheKey && this.cache.has(cacheKey)) {
        const cachedResult = this.cache.get(cacheKey);
        if (this.config.enableLogging) {
          console.log(`Использован кэшированный результат для шаблона ${blueprintId}`);
        }
        return cachedResult;
      }

      // Генерируем результат
      const result = await blueprint.generate({
        parameters,
        parentElement: options?.parentElement,
        idPrefix: options?.idPrefix,
        options: {
          validateParameters: this.config.defaultValidation,
          generateMetadata: true,
          preserveExisting: false,
          dryRun: options?.dryRun || false
        }
      });

      // Кэшируем результат
      if (cacheKey && !options?.dryRun) {
        this.addToCache(cacheKey, result);
      }

      // Обновляем статистику
      if (!options?.dryRun) {
        this.updateUsageStatistics(blueprintId, parameters);
      }

      this.emit('blueprint-generated', { blueprintId, result });

      if (this.config.enableLogging) {
        console.log(`Сгенерирована структура из шаблона ${blueprint.name}: ${result.elements.length} элементов`);
      }

      return result;
    } catch (error) {
      this.emit('blueprint-error', { 
        blueprintId, 
        error: error instanceof Error ? error : new Error(String(error))
      });
      throw error;
    }
  }

  /**
   * Валидация параметров шаблона
   */
  async validateBlueprintParameters(
    blueprintId: string,
    parameters: Record<string, any>
  ): Promise<{
    valid: boolean;
    errors: Array<{ parameter: string; message: string }>;
    warnings: Array<{ parameter: string; message: string }>;
  }> {
    const blueprint = this.blueprints.get(blueprintId);
    if (!blueprint) {
      throw new Error(`Шаблон с ID '${blueprintId}' не найден`);
    }

    return blueprint.validateParameters(parameters);
  }

  /**
   * Получение статистики использования
   */
  async getUsageStatistics(blueprintId?: string): Promise<BlueprintUsageStatistics[]> {
    if (blueprintId) {
      const stats = this.usageStats.get(blueprintId);
      return stats ? [stats] : [];
    }
    return Array.from(this.usageStats.values());
  }

  /**
   * Экспорт шаблона
   */
  async exportBlueprint(blueprintId: string): Promise<string> {
    const blueprint = this.blueprints.get(blueprintId);
    if (!blueprint) {
      throw new Error(`Шаблон с ID '${blueprintId}' не найден`);
    }

    const exportData = {
      id: blueprint.id,
      name: blueprint.name,
      description: blueprint.description,
      type: blueprint.type,
      category: blueprint.category,
      version: blueprint.version,
      metadata: blueprint.metadata,
      parameterGroups: blueprint.parameterGroups,
      exportedAt: new Date().toISOString(),
      exportedBy: 'CSElement BlueprintManager'
    };

    return JSON.stringify(exportData, null, 2);
  }

  /**
   * Импорт шаблона
   */
  async importBlueprint(blueprintData: string): Promise<IBlueprint> {
    try {
      const data = JSON.parse(blueprintData);
      
      // Валидация импортируемых данных
      if (!data.id || !data.name || !data.type || !data.category) {
        throw new Error('Неполные данные шаблона для импорта');
      }

      // Создаем шаблон через фабрику (если это встроенный тип) или как пользовательский
      let blueprint: IBlueprint;
      
      if (this.factory.getAvailableTypes().includes(data.type)) {
        blueprint = this.factory.createBlueprint(data.type, data);
      } else {
        // Создаем пользовательский шаблон
        blueprint = this.factory.createCustomBlueprint({
          id: data.id,
          name: data.name,
          description: data.description,
          generator: async (context: BlueprintGenerationContext) => {
            // Реальная реализация генератора на основе импортированных данных
            const { parameters, parentElement, idPrefix = '' } = context;
            const elements: ICSElement[] = [];
            const warnings: string[] = [];

            try {
              // Если в импортированных данных есть структура элементов, используем её
              if (data.structure && Array.isArray(data.structure)) {
                for (const elementData of data.structure) {
                  try {
                    // Создаем элемент на основе импортированной структуры
                    const element = new (await import('../core/CSElement')).CSElement(
                      elementData.name || `Элемент ${elements.length + 1}`,
                      {
                        data: new Map()
                      }
                    );
                    
                    // Устанавливаем ID если он есть
                    if (elementData.id) {
                      element.setData('originalId', elementData.id);
                      element.setData('prefixedId', `${idPrefix}${elementData.id}`);
                    }

                    // Применяем данные элемента
                    if (elementData.data) {
                      for (const [key, value] of Object.entries(elementData.data)) {
                        element.setData(key, value);
                      }
                    }

                    // Применяем параметры из контекста генерации
                    for (const [key, value] of Object.entries(parameters)) {
                      if (key.startsWith('element_')) {
                        element.setData(key.replace('element_', ''), value);
                      }
                    }

                    // Добавляем к родительскому элементу если указан
                    if (parentElement) {
                      parentElement.addElement(element);
                    }

                    elements.push(element);
                  } catch (error) {
                    warnings.push(`Ошибка создания элемента: ${error instanceof Error ? error.message : String(error)}`);
                  }
                }
              } else {
                // Создаем базовую структуру на основе параметров
                const elementCount = parameters.elementCount || 1;
                const elementPrefix = parameters.elementPrefix || 'item';

                for (let i = 0; i < elementCount; i++) {
                  try {
                    const element = new (await import('../core/CSElement')).CSElement(
                      parameters.elementName ? `${parameters.elementName} ${i + 1}` : `${elementPrefix} ${i + 1}`,
                      {
                        data: new Map()
                      }
                    );
                    
                    // Устанавливаем ID с префиксом
                    element.setData('generatedId', `${idPrefix}${elementPrefix}_${i}`);

                    // Применяем общие параметры
                    if (parameters.defaultData && typeof parameters.defaultData === 'object') {
                      for (const [key, value] of Object.entries(parameters.defaultData)) {
                        element.setData(key, value);
                      }
                    }

                    // Добавляем специфичные для элемента данные
                    element.setData('index', i);
                    element.setData('blueprintId', data.id);
                    element.setData('importedAt', new Date().toISOString());

                    if (parentElement) {
                      parentElement.addElement(element);
                    }

                    elements.push(element);
                  } catch (error) {
                    warnings.push(`Ошибка создания элемента ${i}: ${error instanceof Error ? error.message : String(error)}`);
                  }
                }
              }

              // Добавляем метаданные о генерации
              const metadata = {
                blueprintId: data.id,
                blueprintName: data.name,
                blueprintType: data.type,
                generatedAt: new Date().toISOString(),
                parametersUsed: Object.keys(parameters),
                importedBlueprint: true
              };

              return {
                elements,
                rootElement: parentElement || (elements.length > 0 ? elements[0] : undefined),
                metadata,
                warnings: warnings.length > 0 ? warnings : undefined
              };

            } catch (error) {
              // В случае критической ошибки возвращаем пустой результат с предупреждением
              return {
                elements: [],
                warnings: [`Критическая ошибка генерации: ${error instanceof Error ? error.message : String(error)}`]
              };
            }
          },
          parameters: data.parameterGroups?.flatMap((g: any) => g.parameters) || [],
          metadata: data.metadata
        });
      }

      // Регистрируем импортированный шаблон
      await this.registerBlueprint(blueprint);

      return blueprint;
    } catch (error) {
      throw new Error(`Ошибка импорта шаблона: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  /**
   * Очистка кэша
   */
  async clearCache(): Promise<void> {
    this.cache.clear();
    this.emit('cache-cleared', {});
    
    if (this.config.enableLogging) {
      console.log('Кэш шаблонов очищен');
    }
  }

  /**
   * Получение рекомендаций шаблонов
   */
  async getRecommendations(context?: {
    usageHistory?: string[];
    currentProject?: any;
    userPreferences?: any;
  }): Promise<IBlueprint[]> {
    const allBlueprints = Array.from(this.blueprints.values());
    
    // Простая логика рекомендаций на основе популярности и истории использования
    const recommendations = allBlueprints
      .map(bp => {
        let score = 0;
        
        // Популярность
        const stats = this.usageStats.get(bp.id);
        if (stats) {
          score += stats.usageCount * 0.1;
        }
        
        // История использования
        if (context?.usageHistory?.includes(bp.id)) {
          score += 5;
        }
        
        // Категория и теги (простая эвристика)
        if (bp.category === 'business') score += 1;
        if (bp.metadata.tags?.includes('popular')) score += 2;
        
        return { blueprint: bp, score };
      })
      .sort((a, b) => b.score - a.score)
      .slice(0, 5)
      .map(item => item.blueprint);

    return recommendations;
  }

  /**
   * Добавление в кэш с учетом лимитов
   */
  private addToCache(key: string, value: any): void {
    if (!this.config.enableCaching) return;

    // Проверяем лимит кэша
    if (this.cache.size >= (this.config.maxCacheSize || 100)) {
      // Удаляем самый старый элемент (простая стратегия FIFO)
      const firstKey = this.cache.keys().next().value;
      if (firstKey) {
        this.cache.delete(firstKey);
      }
    }

    this.cache.set(key, value);
  }

  /**
   * Обновление статистики использования
   */
  private updateUsageStatistics(blueprintId: string, parameters: Record<string, any>): void {
    let stats = this.usageStats.get(blueprintId);
    if (!stats) {
      stats = {
        blueprintId,
        usageCount: 0,
        lastUsed: new Date(),
        popularParameterCombinations: []
      };
    }

    stats.usageCount++;
    stats.lastUsed = new Date();

    // Обновляем популярные комбинации параметров
    if (!stats.popularParameterCombinations) {
      stats.popularParameterCombinations = [];
    }

    const existingCombo = stats.popularParameterCombinations.find(combo =>
      JSON.stringify(combo.parameters) === JSON.stringify(parameters)
    );

    if (existingCombo) {
      existingCombo.count++;
    } else {
      stats.popularParameterCombinations.push({
        parameters: { ...parameters },
        count: 1
      });
    }

    // Ограничиваем количество сохраняемых комбинаций
    stats.popularParameterCombinations = stats.popularParameterCombinations
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);

    this.usageStats.set(blueprintId, stats);
    this.emit('statistics-updated', { statistics: [stats] });
  }
}

// Типизация событий для TypeScript
export interface BlueprintManagerEventEmitter {
  on<K extends keyof BlueprintManagerEvents>(
    event: K,
    listener: (args: BlueprintManagerEvents[K]) => void
  ): this;
  
  emit<K extends keyof BlueprintManagerEvents>(
    event: K,
    args: BlueprintManagerEvents[K]
  ): boolean;
} 